import React, { useEffect, useMemo, useState } from 'react'
import { FocusableComponentLayout } from '@noriginmedia/norigin-spatial-navigation'
import { useHybridFocusable } from '../../hooks/useHybridFocusable'
import { useNavigate } from 'react-router-dom'
import tts from '../../lib/TTS.js'
import urls from '../../lib/urls.ts'
import FocusableButtonTS from '../../components/FocusableButton/FocusableButtonTS.tsx'
import StationPassportTitle from '../../components/StationPassportTitle/StationPassportTitle.js'
import ActiveBadge from '../../components/ActiveBadge'
import { useAppSelector } from '../../hooks/useStore'

import {
  ManageMyDonationsProps,
  passportStatus,
  ManageDonationComponentsEnums,
} from './types.ts'

const TITLE = 'Manage My Donations'
const MEMBER_SINCE = 'Member since'
const DONATION_AMOUNT_TEXT = 'Donation Amount:'
const BILLING_DATE_TEXT = 'Next Billing Date:'
const MORE_INFORMATION =
  'For more information or to make another donation visit '

const BACK_BUTTON_TEXT = 'Go back'
const CANCEL_BUTTON_TEXT = 'Cancel Recurring Donation'
const CONFIRM_ENABLE_BUTTON_TEXT = 'Confirm & Enable Passport'

const COMPONENT_FOCUS_KEY = 'MANAGE_MY_DONATIONS'
const BACK_BUTTON_FOCUS_KEY = `${COMPONENT_FOCUS_KEY}_BACK_BUTTON`
const CANCEL_BUTTON_FOCUS_KEY = `${COMPONENT_FOCUS_KEY}_CANCEL_BUTTON`
const CONFIRM_BUTTON_FOCUS_KEY = `${COMPONENT_FOCUS_KEY}_CONFIRM_BUTTON`

const ManageMyDonation = ({
  donationInfo,
  passportActivationStatus,
  handleButtonEnter: handleButtonEnterFromProps,
}: ManageMyDonationsProps) => {
  const [keyButtonFocused, setKeyButtonFocused] = useState<string>('')
  const [readWholeScreen, setReadWholeScreen] = useState<boolean>(false)
  const [textButtonFocused, setTextButtonFocused] = useState<string>('')
  const { ref } = useHybridFocusable({})
  const navigate = useNavigate()

  const { first_name: firstName, last_name: lastName } = useAppSelector(
    (state) => state.user
  )

  const buttonTTS = useMemo(
    () => `Focused on ${textButtonFocused} Button.`,
    [textButtonFocused]
  )

  const { amount, interval, memberDate, nextBilling, shortNameStation } =
    donationInfo

  useEffect(() => {
    tts.stopRead()

    if (!readWholeScreen && keyButtonFocused === BACK_BUTTON_FOCUS_KEY) {
      tts.addTextToRead(
        `${TITLE} .... ${MEMBER_SINCE} .... ${DONATION_AMOUNT_TEXT} .... ${BILLING_DATE_TEXT} .... ${MORE_INFORMATION}${
          urls.PBS_URL
        }${'.'} ${buttonTTS}`
      )
      setReadWholeScreen(true)
    } else {
      tts.addTextToRead(buttonTTS)
    }
  }, [keyButtonFocused, textButtonFocused])

  const handleButtonFocus = (
    layout: FocusableComponentLayout,
    extraProps: { key: string; ttsFocus: string }
  ) => {
    setKeyButtonFocused(extraProps.key)
    setTextButtonFocused(extraProps.ttsFocus)
  }

  const handleButtonEnter = (extraProps: {
    key: string
    ttsFocus: string
    route: ManageDonationComponentsEnums
  }) => {
    handleButtonEnterFromProps(extraProps.route)
  }

  const handleConfirmButtonEnter = () => {
    navigate('/passport-activation', {
      state: {
        stationTitle: shortNameStation,
        fromSettings: true,
      },
    })
  }

  return (
    <>
      <div ref={ref} className="cancellation-donation-screen">
        <div className="cancellation-donation-screen__wrapper">
          <div className="cancellation-donation-screen__manage">
            <h1 className="cancellation-donation-screen__manage__title">
              {TITLE}
            </h1>
            <div className="cancellation-donation-screen__manage__user-info">
              <div className="user-icon">
                {lastName.charAt(0).toUpperCase()}
              </div>
              <span className="user-name">{`${firstName.toLowerCase()} ${lastName.toLowerCase()}`}</span>
            </div>
            <div className="cancellation-donation-screen__manage__info">
              <div>
                <div className="cancellation-donation-screen__manage__passport-info">
                  <div className="station-passport-info">
                    <StationPassportTitle stationTitle={shortNameStation} />
                  </div>
                  <ActiveBadge
                    isActive={
                      passportActivationStatus === passportStatus.activated
                    }
                  />
                </div>
                <p
                  className="cancellation-donation-screen__manage__member-info"
                  style={{ marginBottom: '40px' }}
                >
                  {MEMBER_SINCE} {memberDate}
                </p>
              </div>
              <div className="cancellation-donation-screen__manage__amount-info">
                <p
                  className="cancellation-donation-screen__manage__donation-info"
                  style={{ marginBottom: '30px' }}
                >
                  <span className="cancellation-donation-screen__manage__donation-info">{`${DONATION_AMOUNT_TEXT}`}</span>
                  <span className="cancellation-donation-screen__manage__donation-info__value">
                    &nbsp;&nbsp;${amount}/{interval}
                  </span>
                </p>
                <p
                  className="cancellation-donation-screen__manage__donation-info"
                  style={{ marginBottom: '40px' }}
                >
                  <span className="cancellation-donation-screen__donation-info">{`${BILLING_DATE_TEXT}`}</span>
                  <span className="cancellation-donation-screen__manage__donation-info__value">
                    &nbsp;&nbsp;{nextBilling}
                  </span>
                </p>
              </div>
              <p className="cancellation-donation-screen__manage__member-info">
                {MORE_INFORMATION}
                <a
                  className="cancellation-donation-screen__manage__member-info__link"
                  href={urls.DONATE_URL}
                >
                  {' '}
                  {urls.PBS_URL}
                </a>
              </p>
            </div>
            <div className="cancellation-donation-screen__manage__container-buttons">
              <FocusableButtonTS<{ key: string; ttsFocus: string }>
                className="cancellation-donation-screen__button"
                extraProps={{
                  key: BACK_BUTTON_FOCUS_KEY,
                  ttsFocus: BACK_BUTTON_TEXT,
                }}
                focusKey={BACK_BUTTON_FOCUS_KEY}
                onEnterPress={() => navigate(-1)}
                onFocus={handleButtonFocus}
                stealFocus={true}
              >
                {BACK_BUTTON_TEXT}
              </FocusableButtonTS>
              {passportActivationStatus !== passportStatus.activated && (
                <FocusableButtonTS<{ key: string; ttsFocus: string }>
                  className="cancellation-donation-screen__button"
                  extraProps={{
                    key: CONFIRM_BUTTON_FOCUS_KEY,
                    ttsFocus: CONFIRM_ENABLE_BUTTON_TEXT,
                  }}
                  focusKey={CONFIRM_BUTTON_FOCUS_KEY}
                  onEnterPress={handleConfirmButtonEnter}
                  onFocus={handleButtonFocus}
                >
                  {CONFIRM_ENABLE_BUTTON_TEXT}
                </FocusableButtonTS>
              )}
              <FocusableButtonTS<{
                key: string
                ttsFocus: string
                route: ManageDonationComponentsEnums
              }>
                className="cancellation-donation-screen__button"
                extraProps={{
                  key: CANCEL_BUTTON_FOCUS_KEY,
                  ttsFocus: CANCEL_BUTTON_TEXT,
                  route: ManageDonationComponentsEnums.CANCEL_DONATION,
                }}
                focusKey={CANCEL_BUTTON_FOCUS_KEY}
                onEnterPress={handleButtonEnter}
                onFocus={handleButtonFocus}
              >
                {CANCEL_BUTTON_TEXT}
              </FocusableButtonTS>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default ManageMyDonation
